// ===================================
// ACTION DETAIL VIEW COMPONENT - src/components/ActionDetail.tsx
// ===================================
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import ActionsApi from '../services/actionsApi';
import { parseUserPermissions } from '../utils/permissions';
import { Action, Topic, ActionField } from '../types';
import { 
  X, 
  Edit, 
  Trash2, 
  Calendar, 
  Hash,
  Code,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Copy,
  FileText,
  Settings,
  Eye,
  Database,
  ArrowRight
} from 'lucide-react';
import Button from './ui/button';
import Badge from './ui/badge';

interface ActionDetailProps {
  isOpen: boolean;
  action: Action;
  topic: Topic;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const ActionDetail: React.FC<ActionDetailProps> = ({
  isOpen,
  action,
  topic,
  onClose,
  onEdit,
  onDelete
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const permissions = parseUserPermissions(user);

  const [flowValidating, setFlowValidating] = useState(false);
  const [flowStatus, setFlowStatus] = useState(action.flow_status || 'unknown');

  // Normalize action to handle legacy formats
  const normalizedAction = ActionsApi.normalizeAction(action);

  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Validate flow
  const validateFlow = async () => {
    try {
      setFlowValidating(true);
      const result = await ActionsApi.validateFlow({ flow_name: action.flow_name });
      
      setFlowStatus(result.exists ? 'valid' : 'invalid');
      
      addNotification({
        type: result.exists ? 'success' : 'warning',
        title: 'Flow Validation',
        message: result.exists 
          ? `Flow "${action.flow_name}" found in Salesforce`
          : `Flow "${action.flow_name}" not found in Salesforce`
      });

    } catch (err) {
      const error = ActionsApi.handleApiError(err);
      setFlowStatus('unknown');
      addNotification({
        type: 'error',
        title: 'Flow validation failed',
        message: error.message
      });
    } finally {
      setFlowValidating(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      addNotification({
        type: 'success',
        title: 'Copied',
        message: `${label} copied to clipboard`
      });
    }).catch(() => {
      addNotification({
        type: 'error',
        title: 'Copy failed',
        message: `Failed to copy ${label}`
      });
    });
  };

  // Get flow status badge
  const getFlowStatusBadge = () => {
    switch (flowStatus) {
      case 'valid':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Valid
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Invalid
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Unknown
          </Badge>
        );
    }
  };

  // Render field type badge
  const getFieldTypeBadge = (type: string) => {
    const colors = {
      string: 'bg-blue-100 text-blue-800',
      number: 'bg-green-100 text-green-800',
      boolean: 'bg-purple-100 text-purple-800',
      picklist: 'bg-orange-100 text-orange-800',
      multiselect: 'bg-pink-100 text-pink-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  // Render field constraints
  const renderFieldConstraints = (field: ActionField) => {
    if (!field.constraints || Object.keys(field.constraints).length === 0) {
      return null;
    }

    return (
      <div className="mt-2 text-xs text-gray-600">
        {field.constraints.min !== undefined && (
          <span className="mr-2">Min: {field.constraints.min}</span>
        )}
        {field.constraints.max !== undefined && (
          <span className="mr-2">Max: {field.constraints.max}</span>
        )}
        {field.constraints.values && field.constraints.values.length > 0 && (
          <div className="mt-1">
            <span className="font-medium">Values:</span> {field.constraints.values.slice(0, 3).join(', ')}
            {field.constraints.values.length > 3 && ` ... +${field.constraints.values.length - 3} more`}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">{normalizedAction.action_name}</h2>
              <Badge variant="outline">
                {topic.name}
              </Badge>
            </div>
            {normalizedAction.description && (
              <p className="text-gray-600 mt-1">{normalizedAction.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {permissions.canUpdate && (
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            
            {permissions.canDelete && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onDelete}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Basic Information
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">ID:</span>
                  <span className="text-sm font-medium">{normalizedAction.id}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(normalizedAction.id.toString(), 'Action ID')}
                    className="h-6 w-6 p-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Topic ID:</span>
                  <span className="text-sm font-medium">{normalizedAction.topic_id}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Created:</span>
                  <span className="text-sm font-medium">
                    {new Date(normalizedAction.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Updated:</span>
                  <span className="text-sm font-medium">
                    {new Date(normalizedAction.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Code className="h-5 w-5" />
                Salesforce Flow
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-600">Flow Name:</span>
                    <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {normalizedAction.flow_name}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(normalizedAction.flow_name, 'Flow name')}
                      className="h-6 w-6 p-0"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Status:</span>
                  {getFlowStatusBadge()}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={validateFlow}
                    disabled={flowValidating}
                    className="h-6 w-6 p-0"
                  >
                    {flowValidating ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                  </Button>
                </div>
                
                {normalizedAction.flow_last_checked && (
                  <div className="text-xs text-gray-500">
                    Last checked: {new Date(normalizedAction.flow_last_checked).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Field Statistics
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{normalizedAction.input_count}</div>
                <div className="text-sm text-gray-600">Input Fields</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{normalizedAction.output_count}</div>
                <div className="text-sm text-gray-600">Output Fields</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{normalizedAction.required_count}</div>
                <div className="text-sm text-gray-600">Required Fields</div>
              </div>
            </div>
          </div>

          {/* Input Parameters */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Input Parameters ({normalizedAction.input_count})
            </h3>
            
            {normalizedAction.input_params.length > 0 ? (
              <div className="space-y-3">
                {normalizedAction.input_params.map((field: ActionField, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <code className="text-sm font-medium text-gray-900">{field.name}</code>
                          {getFieldTypeBadge(field.type)}
                          {normalizedAction.required_fields.includes(field.name) && (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          )}
                        </div>
                        
                        {field.sample_value && (
                          <div className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Sample:</span> {field.sample_value}
                          </div>
                        )}
                        
                        {renderFieldConstraints(field)}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(field.name, 'Field name')}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No input parameters defined</p>
              </div>
            )}
          </div>

          {/* Output Parameters */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <ArrowRight className="h-5 w-5 rotate-180" />
              Output Parameters ({normalizedAction.output_count})
            </h3>
            
            {normalizedAction.output_params.length > 0 ? (
              <div className="space-y-3">
                {normalizedAction.output_params.map((field: ActionField, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <code className="text-sm font-medium text-gray-900">{field.name}</code>
                          {getFieldTypeBadge(field.type)}
                        </div>
                        
                        {renderFieldConstraints(field)}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(field.name, 'Field name')}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No output parameters defined</p>
              </div>
            )}
          </div>

          {/* Example Use Case */}
          {normalizedAction.example_use_case && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Example Use Case</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">{normalizedAction.example_use_case}</p>
              </div>
            </div>
          )}

          {/* Sample Data */}
          {normalizedAction.sample_data && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Code className="h-5 w-5" />
                Sample Data
              </h3>
              <div className="bg-gray-900 rounded-lg p-4 relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(
                    JSON.stringify(normalizedAction.sample_data, null, 2), 
                    'Sample data'
                  )}
                  className="absolute top-2 right-2 h-6 w-6 p-0 text-gray-400 hover:text-white"
                >
                  <Copy className="h-3 w-3" />
                </Button>
                <pre className="text-sm text-green-400 overflow-x-auto">
                  {JSON.stringify(normalizedAction.sample_data, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Required Fields Summary */}
          {normalizedAction.required_fields.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Required Fields</h3>
              <div className="flex flex-wrap gap-2">
                {normalizedAction.required_fields.map(fieldName => (
                  <Badge key={fieldName} variant="destructive" className="flex items-center gap-1">
                    {fieldName}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(fieldName, 'Field name')}
                      className="h-4 w-4 p-0 hover:bg-white hover:bg-opacity-20"
                    >
                      <Copy className="h-2 w-2" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionDetail;